import {useEffect, useRef} from 'react';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import {SoopAccordion, SoopButtonCommon, SoopLoading} from '@soop-ui/soop-ui';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import {sendHelpClickLog} from '@/utils/help/log';
import {useEvaluateFaq, useGetFaqBody} from '@/hooks/help';
import {HELP_LOG} from '@/constants/help';
import styles from './FaqItem.module.scss';

const BEFORE_EVALUATE = 'idle';

interface FaqItemProps {
  item: {title: string; kbId: string};
  isExpanded: boolean;
  onExpand: () => void;
}

const FaqItem = ({item, isExpanded, onExpand}: FaqItemProps) => {
  const {isMobile} = useHelpContext();
  const {data, isLoading, isFetched} = useGetFaqBody({
    enabled: isExpanded,
    questionId: item.kbId,
  });
  const {mutate: evaluateFaq, status} = useEvaluateFaq();
  const handleEvaluateFaq = (isSatisfied: boolean) => {
    evaluateFaq({
      faqId: item.kbId,
      isSatisfied,
    });
    sendHelpClickLog(HELP_LOG.CODE_TYPE.FAQ, {
      buttonType: HELP_LOG.BUTTON_TYPE.FAQ_FEEDBACK,
      actionType: isSatisfied
        ? HELP_LOG.ACTION_TYPE.TO_YES
        : HELP_LOG.ACTION_TYPE.TO_NO,
      text: item.title,
    });
  };

  const accordionRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isExpanded && isFetched) {
      requestAnimationFrame(() => {
        if (accordionRef.current) {
          accordionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    }
  }, [isExpanded, isFetched]);

  return (
    <SoopAccordion
      ref={accordionRef}
      fullWidth
      title={item.title}
      isExpanded={isExpanded}
      onExpand={onExpand}
      variant='divider'
      className={clsx(styles.FaqAccordion, isExpanded && styles.opened)}>
      {isLoading ? (
        <SoopLoading isDevice={isMobile ? 'mobile' : 'desktop'} />
      ) : (
        <>
          <div className={styles.AnswerArea}>
            <span className={styles.AnswerTitle}>A</span>
            <div
              className={styles.AnswerContent}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data?.contents || '', {
                  ADD_ATTR: ['target'],
                }),
              }}
            />
          </div>
          <div className={styles.FeedbackArea}>
            {status !== BEFORE_EVALUATE ? (
              '소중한 의견 감사드립니다.'
            ) : (
              <>
                도움이 되었나요?
                <div className={styles.FeedbackBtn}>
                  {['예', '아니오'].map((answer) => (
                    <SoopButtonCommon
                      key={answer}
                      variant='defaultTertiary'
                      size={32}
                      onClick={() => handleEvaluateFaq(answer === '예')}>
                      {answer}
                    </SoopButtonCommon>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </SoopAccordion>
  );
};

export default FaqItem;
